import { useEffect } from 'react';
import { useCurrentStateAndParams } from '@uirouter/react';
import { useFormikContext, Field } from 'formik';

import { FormControl } from '@@/form-components/FormControl';
import { FormSection } from '@@/form-components/FormSection';
import { PortainerSelect } from '@@/form-components/PortainerSelect';
import { Input } from '@@/form-components/Input';
import { Alert } from '@@/Alert';
import { Link } from '@@/Link';

import { useAddon } from '../queries/useAddon';
import { useCatalogError } from '../queries/useAddons';
import { useAddonChartSourceStatus } from '../queries/useAddonChartSourceStatus';

import { InstallFormValues } from './types';
import { hasAnyValue } from './addonValues';
import { ChartSourceFormSection } from './ChartSourceFormSection';
import { AddonImageFormSection } from './AddonImageFormSection';
import { useAddonChartSource } from './useAddonChartSource';

/**
 * These stand in for the configuration form when there is no chart reference to
 * read versions or default values from. Which one applies depends on why it is
 * missing; see catalog-messages.
 */
function CatalogUnavailableWarning() {
  return (
    <Alert color="warn" title="Add-on configuration unavailable">
      <p className="mb-0">
        The add-on catalog could not be loaded, so this add-on&apos;s chart
        versions and configuration options are unknown. The add-on itself is
        unaffected and keeps running.
      </p>
      <p className="mb-0">
        Check that Portainer can reach the catalog source, or set a different
        URL in{' '}
        <Link
          to="portainer.settings"
          params={{ section: 'addons-settings' }}
          data-cy="addon-config-settings-link"
        >
          add-on settings
        </Link>
        .
      </p>
    </Alert>
  );
}

function AddonNotInCatalogWarning() {
  return (
    <Alert color="warn" title="Add-on not in the catalog">
      <p className="mb-0">
        This add-on is not listed in the current add-on catalog, so its chart
        versions and configuration options are unknown. The add-on itself is
        unaffected and keeps running.
      </p>
      <p className="mb-0">
        Check that the catalog configured in{' '}
        <Link
          to="portainer.settings"
          params={{ section: 'addons-settings' }}
          data-cy="addon-config-settings-link"
        >
          add-on settings
        </Link>{' '}
        lists this add-on.
      </p>
    </Alert>
  );
}

/**
 * The add-on deployment form, shared by the install wizard and the configuration
 * tab. Fields are fixed, not generated from values.yaml: an add-on's own settings
 * live in Portainer's config store, leaving only image and storage here.
 */
export function ConfigurationStep({
  readOnly = false,
  chartSourceReadOnly = false,
}: {
  readOnly?: boolean;
  // The chart source is chosen once, at install time: a live release is never
  // re-pointed at another registry. The detail view sets this so the source stays
  // fixed while the version and values remain editable for an upgrade.
  chartSourceReadOnly?: boolean;
}) {
  const { params } = useCurrentStateAndParams();
  const addonId = String(params.id ?? '');
  const {
    initialValues,
    values: { version, chartConfirmed },
    setFieldValue,
  } = useFormikContext<InstallFormValues>();

  // initialValues, not live values: keying this to what the user is typing would
  // let the section fold itself mid-edit.
  const hasAdvancedValues = hasAnyValue(initialValues.values);

  const addonQuery = useAddon(addonId);
  const record = addonQuery.data?.record;
  const chartSource = record?.helmChartRepository ?? '';
  // Tells an unreachable catalog apart from one that simply does not list this
  // add-on, which need different remedies.
  const catalogError = useCatalogError();

  const source = useAddonChartSource({ addonId, fixed: chartSourceReadOnly });

  // A chart typed by hand is checked only once it is settled — matching
  // chartConfirmed — not on every keystroke of a path still being edited. The
  // installed source has no such field to type into, so it is always checked
  // as-is.
  const checkedChart =
    chartSourceReadOnly || chartConfirmed === source.chart ? source.chart : '';

  // The versions come from a live check of the chosen source rather than from the
  // record, for both sources alike: the record describes the source the add-on
  // resolves to, while the form works against the one the user has picked, and
  // the check is also what says whether that source can be read at all.
  const sourceStatus = useAddonChartSourceStatus({
    addonId,
    registryId: source.registryId,
    chart: checkedChart,
  });
  const status = sourceStatus.data;
  const versions = status?.versions ?? [];

  // A source whose check has finished: only then does an empty version list mean
  // the chart resolved to nothing, rather than "still looking".
  const isChecked = sourceStatus.isFetched && !!status?.reachable;

  // Picking a registry for the chart is the point at which the image stops being
  // optional: an air-gapped install mirrors chart and image together, and the
  // image is what the release pulls once Helm runs. Folded away it is the step
  // nothing on the visible form asks for. Only while the source can still be
  // chosen — an installed add-on's is fixed, and there is nothing to react to.
  const imageNeedsAttention = !chartSourceReadOnly && !!source.registryId;

  // On a fresh install the version field starts empty - default it to the latest
  // (the head of the list) once the versions load.
  const latestVersion = versions[0];
  useEffect(() => {
    if (version || !latestVersion) {
      return;
    }
    setFieldValue('version', latestVersion);
  }, [version, latestVersion, setFieldValue]);

  // Only an add-on that could not be loaded at all: without it there is no
  // add-on to configure, and nothing below has anything to work against.
  if (addonQuery.isError) {
    return <Alert color="error">Unable to load the add-on.</Alert>;
  }

  // A record with no chart source and no registry of its own means nothing
  // describes where this add-on's chart comes from — the catalog could not be
  // loaded, or no longer lists it, which need different remedies. Warned about
  // above the form rather than instead of it: the way out of an air-gapped
  // install is to pull the chart from a registry, which is chosen on the very
  // fields that blanking the form takes away.
  const catalogWarning =
    record &&
    !chartSource &&
    !source.registryId &&
    (catalogError ? (
      <CatalogUnavailableWarning />
    ) : (
      <AddonNotInCatalogWarning />
    ));

  return (
    // The section opens the form, so the title's default top margin has nothing
    // to separate it from and reads as a blank band above the first field.
    <FormSection title="Deployment" titleClassName="!mt-0">
      {catalogWarning}

      <ChartSourceFormSection
        source={source}
        status={status}
        isStatusLoading={sourceStatus.isInitialLoading}
        isLoading={addonQuery.isLoading}
        readOnly={chartSourceReadOnly}
      />

      <FormControl
        label="Version"
        size="vertical"
        inputId="addon-install-version-select"
        // Reported here rather than as a notification: the chart path is typed
        // freely, so a lookup finding nothing usually just means the path is
        // unfinished or wrong, and belongs next to the field.
        errors={versionError()}
      >
        <PortainerSelect<string>
          inputId="addon-install-version-select"
          data-cy="addon-install-version-select"
          value={version}
          options={versions.map((v, i) => ({
            label: i === 0 ? `${v} (latest)` : v,
            value: v,
          }))}
          // isInitialLoading, not isLoading: the check is disabled until a chart
          // is picked, and a disabled query reports isLoading forever — which
          // spun the field before there was anything to look up.
          isLoading={sourceStatus.isInitialLoading}
          onChange={(v) => setFieldValue('version', v ?? '')}
          placeholder={versionPlaceholder()}
          disabled={readOnly || !versions.length}
        />
      </FormControl>

      {/* Every field falls back to a default, so the section is folded away to keep
          the form to what an install requires. It starts open when a value is
          already set, so an existing override is never hidden.
          Keyed on that, because a section's folded state is settled at mount:
          remounting is what lets a source picked later open it. */}
      <FormSection
        key={imageNeedsAttention ? 'image-shown' : 'image-folded'}
        title="Advanced configuration"
        titleSize="sm"
        isFoldable
        defaultFolded={!hasAdvancedValues && !imageNeedsAttention}
      >
        <AddonImageFormSection readOnly={readOnly} />

        <FormControl
          label="Storage class"
          size="vertical"
          inputId="addon-storage-class"
          tooltip="StorageClass used for the add-on's volume. Leave empty to use the cluster's default."
        >
          <Field
            as={Input}
            id="addon-storage-class"
            name="values.storageClass"
            placeholder="Cluster default"
            readOnly={readOnly}
            disabled={readOnly}
            data-cy="addon-install-storage-class"
          />
        </FormControl>
      </FormSection>
    </FormSection>
  );

  // Why the list is empty, when the reason is known. A source that could not be
  // read is reported against the chart location instead, where the remedy is: an
  // empty version list is a consequence of it, and saying so twice would put the
  // remedy on the field that does not carry it.
  function versionError() {
    return (
      isChecked &&
      !versions.length &&
      'No versions found for this chart. Check the chart path — the registry is reachable but holds no such chart.'
    );
  }

  // Says what to do next rather than just reporting emptiness. An air-gapped
  // install cannot reach the add-on's public chart, so its version list comes back
  // empty — the way out is to pick a registry, and the field should say so.
  function versionPlaceholder() {
    if (versions.length) {
      return 'Select a version...';
    }
    if (source.registryId && !source.chart) {
      return 'Select a chart to list versions';
    }

    // A source that could not be read says so against the chart location, right
    // above; repeating it here would say the same thing twice.
    return 'No versions found';
  }
}
